import React, { useState, useEffect } from 'react';
import { Calculator, Clock, DollarSign } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const TimeOnSiteValueCalculator = () => {
  const [averageTimeOnSite, setAverageTimeOnSite] = useState(5);
  const [averageRevenuePerVisit, setAverageRevenuePerVisit] = useState(10);
  const [timeOnSiteValue, setTimeOnSiteValue] = useState(0);

  useEffect(() => {
    calculateTimeOnSiteValue();
  }, [averageTimeOnSite, averageRevenuePerVisit]);

  const calculateTimeOnSiteValue = () => {
    const value = (averageRevenuePerVisit / averageTimeOnSite).toFixed(2);
    setTimeOnSiteValue(parseFloat(value));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/time-on-site-value-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Time on Site Value Calculator: Measure Engagement Worth | Free Tool</title>
        <meta name="description" content="Calculate your Time on Site Value instantly. Compare with industry benchmarks, get engagement optimization tips, and improve visitor value." />
        <meta name="keywords" content="time on site value calculator, engagement metrics, visitor value, session duration ROI, user engagement analytics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Time on Site Value Calculator: Optimize Engagement Worth" />
        <meta property="og:description" content="Calculate your Time on Site Value instantly. Get actionable insights to improve visitor engagement and value." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Time on Site Value Calculator: Optimize Engagement Worth" />
        <meta property="twitter:description" content="Calculate your Time on Site Value instantly. Get actionable insights to improve visitor engagement and value." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Time on Site Value Calculator",
            "description": "Free calculator to measure and optimize the monetary value of visitor engagement time",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Time Value",
              "Industry Benchmarks",
              "Engagement Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Time on Site Value",
              "description": "The monetary value generated per minute of visitor engagement on a website."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Digital Marketers, Website Owners, UX Designers"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Time on Site Value",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter average time on site in minutes"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input average revenue per visit"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your time value and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Time on Site Value Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Average Time on Site (minutes)</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Clock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={averageTimeOnSite}
                  onChange={(e) => setAverageTimeOnSite(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Average Revenue Per Visit</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={averageRevenuePerVisit}
                  onChange={(e) => setAverageRevenuePerVisit(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${timeOnSiteValue}
            <span className="text-xl text-gray-500 ml-2">per minute</span>
          </div>
          <p className="text-gray-600">Time on Site Value</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Time on Site Value is ${timeOnSiteValue} per minute. This means that each minute a visitor spends on your site generates ${timeOnSiteValue} in revenue.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Average Time on Site by Industry</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>E-commerce: 3-4 minutes</li>
          <li>B2B Sites: 2-3 minutes</li>
          <li>Media Sites: 4-5 minutes</li>
          <li>Blog Sites: 2-3 minutes</li>
          <li>SaaS Sites: 3-4 minutes</li>
          <li>Education: 4-6 minutes</li>
          <li>Retail: 2-3 minutes</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Engagement Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Create engaging content</li>
          <li>Improve navigation</li>
          <li>Implement internal linking</li>
          <li>Use multimedia content</li>
          <li>Optimize load times</li>
          <li>Personalize content</li>
          <li>Add recommendations</li>
          <li>Enhance user experience</li>
          <li>Use interactive elements</li>
          <li>Optimize mobile experience</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Content Engagement Factors</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Video content: +88% time on site</li>
          <li>Interactive elements: +52% engagement</li>
          <li>Personalized content: +43% time</li>
          <li>Mobile optimization: +35% duration</li>
          <li>Fast loading: +23% engagement</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Value Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>+1 minute = 15% higher conversion rate</li>
          <li>Engaged users spend 60% more</li>
          <li>Long sessions convert 3x better</li>
          <li>Return visitors stay 2.5x longer</li>
          <li>Mobile users browse 40% less time</li>
        </ul>
      </div>
    </div>
  );
};

export default TimeOnSiteValueCalculator;
